"use client";

import { HoverCard, HoverCardContent, HoverCardTrigger, Button } from "visuais";
import {
  Info,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { usePrenotaStore, useParcelas } from "@PN/inclusao";

interface MissingFields {
  condicaoFinanceira: boolean;
  dataEmissao: boolean;
  valorNF: boolean;
  fornecedor: boolean;
  loja: boolean;
}

const FIELD_LABELS = {
  condicaoFinanceira: "Condição de Pagamento",
  dataEmissao: "Data de Emissão",
  valorNF: "Valor da NF",
  fornecedor: "Fornecedor",
  loja: "Loja",
} as const;

export function ParcelasStatusInfo() {
  const draft = usePrenotaStore((state) => state.draft);
  const { pagamentos, documento } = draft;
  const { parcelas, isLoading, valorNF, canFetch, handleForceRefetch } =
    useParcelas();

  // Deriva missingFields
  const missingFields: MissingFields = {
    condicaoFinanceira: !pagamentos.condicaoFinanceira,
    dataEmissao: !documento.dataEmissao,
    valorNF: !valorNF || valorNF <= 0,
    fornecedor: !documento.fornecedor,
    loja: !documento.loja,
  };

  // Verifica se pode buscar parcelas
  const canFetchParcelas = !Object.values(missingFields).some(Boolean);

  const missingCount = Object.values(missingFields).filter(Boolean).length;
  const missingFieldsList = Object.entries(missingFields)
    .filter(([, isMissing]) => isMissing)
    .map(([field]) => FIELD_LABELS[field as keyof typeof FIELD_LABELS]);

  // Dados PIX
  const hasPixData = !!(pagamentos.chavePix || pagamentos.cnpjPix);

  // Estado 1: Campos faltando
  if (!canFetchParcelas) {
    return (
      <HoverCard>
        <HoverCardTrigger asChild>
          <button className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-muted/50 transition-colors">
            <AlertCircle className="h-3 w-3 text-amber-500" />
          </button>
        </HoverCardTrigger>
        <HoverCardContent className="w-80 p-4" side="top" align="start">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
              <span className="text-sm font-medium">
                Campos obrigatórios pendentes
              </span>
            </div>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>Para carregar as parcelas automaticamente, preencha:</p>
              <ul className="space-y-1.5 ml-2">
                {missingFieldsList.map((fieldLabel) => (
                  <li key={fieldLabel} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0"></span>
                    <span className="text-sm">{fieldLabel}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-1 border-t border-border/50">
                <p className="text-xs text-amber-600 font-medium">
                  {missingCount} campo{missingCount > 1 ? "s" : ""} pendente
                  {missingCount > 1 ? "s" : ""}
                </p>
                {valorNF > 0 && (
                  <p className="text-xs text-blue-600 mt-1">
                    Valor NF atual: R$ {valorNF.toFixed(2).replace(".", ",")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  // Estado 2: Carregando automaticamente
  if (isLoading && canFetch) {
    return (
      <HoverCard>
        <HoverCardTrigger asChild>
          <button className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-muted/50 transition-colors">
            <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />
          </button>
        </HoverCardTrigger>
        <HoverCardContent className="w-72 p-4" side="top" align="start">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 text-blue-500 animate-spin flex-shrink-0" />
              <span className="text-sm font-medium">Buscando parcelas...</span>
            </div>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>Carregando parcelas automaticamente da API</p>
              <div className="flex items-center gap-2">
                <span className="text-blue-600">⏳</span>
                <span>Processando condição de pagamento</span>
              </div>
              {valorNF > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-blue-600">✓</span>
                  <span>
                    Valor NF: R$ {valorNF.toFixed(2).replace(".", ",")}
                  </span>
                </div>
              )}
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  // Estado 3: Dados carregados com sucesso
  if (parcelas.length > 0) {
    return (
      <HoverCard>
        <HoverCardTrigger asChild>
          <div className="flex items-center gap-1 text-xs text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 px-2 py-1 rounded-md border border-cyan-200 dark:border-cyan-800 hover:border-double transition-all duration-300">
            <span className="w-2 h-2 bg-cyan-500 dark:bg-cyan-400 rounded-full animate-pulse"></span>
            <span>Salvo automaticamente</span>
          </div>
        </HoverCardTrigger>
        <HoverCardContent className="w-72 p-4" side="top" align="start">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-sm font-medium">Parcelas carregadas</span>
            </div>
            <div className="text-sm text-muted-foreground space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-green-600">✓</span>
                <span>
                  {parcelas.length} parcela{parcelas.length > 1 ? "s" : ""}{" "}
                  encontrada{parcelas.length > 1 ? "s" : ""}
                </span>
              </div>
              {valorNF > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <span>
                    Valor NF: R$ {valorNF.toFixed(2).replace(".", ",")}
                  </span>
                </div>
              )}
              {pagamentos.condicaoFinanceira && (
                <div className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Condição: {pagamentos.condicaoFinanceira}</span>
                </div>
              )}
              {hasPixData && (
                <div className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <span>PIX configurado</span>
                </div>
              )}
              <div className="pt-1 border-t border-border/50">
                <p className="text-xs text-green-600 font-medium">
                  ✓ Carregamento automático concluído
                </p>
                <div className="mt-2">
                  <Button
                    onClick={handleForceRefetch}
                    size="sm"
                    variant="outline"
                    className="w-full text-xs"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Atualizar parcelas
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  // Estado 4: Sem dados mas pode buscar
  if (canFetch && !isLoading) {
    return (
      <HoverCard>
        <HoverCardTrigger asChild>
          <button className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-muted/50 transition-colors">
            <Info className="h-3 w-3 text-blue-500" />
          </button>
        </HoverCardTrigger>
        <HoverCardContent className="w-72 p-4" side="top" align="start">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-blue-500 flex-shrink-0" />
              <span className="text-sm font-medium">Pronto para buscar</span>
            </div>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>Todos os campos estão preenchidos</p>
              <div className="flex items-center gap-2">
                <span className="text-blue-600">✓</span>
                <span>Condição selecionada</span>
              </div>
              {valorNF > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-blue-600">✓</span>
                  <span>
                    Valor NF: R$ {valorNF.toFixed(2).replace(".", ",")}
                  </span>
                </div>
              )}
              <div className="pt-1 border-t border-border/50">
                <p className="text-xs text-blue-600 font-medium">
                  As parcelas serão carregadas automaticamente
                </p>
              </div>
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  // Estado 5: Sem dados
  return null;
}
